import { useState } from "react";
import {
  getAdminComparatorFirms,
  getAdminComparatorOffers,
  deleteComparatorOffer,
  duplicateComparatorOffer,
  useQuery,
  useAction,
} from "wasp/client/operations";
import type { ComparatorFirm, ComparatorOffer } from "wasp/entities";
import { Button } from "../../../client/components/ui/button";
import { Badge } from "../../../client/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../client/components/ui/select";
import { useToast } from "../../../client/hooks/use-toast";
import { Plus, Pencil, Trash2, Copy, Upload } from "lucide-react";
import OfferFormDialog from "./OfferFormDialog";
import CsvImportDialog from "./CsvImportDialog";

const ALL_FIRMS = "__all__";

export default function OffersTab() {
  const { data: firms } = useQuery(getAdminComparatorFirms);
  const [selectedFirmId, setSelectedFirmId] = useState<string>(ALL_FIRMS);
  const { data: offers, refetch } = useQuery(
    getAdminComparatorOffers,
    selectedFirmId !== ALL_FIRMS ? { firmId: selectedFirmId } : {},
  );

  const deleteOffer = useAction(deleteComparatorOffer);
  const duplicateOffer = useAction(duplicateComparatorOffer);
  const { toast } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ComparatorOffer | null>(null);
  const [csvOpen, setCsvOpen] = useState(false);

  const firmName = (firmId: string) =>
    firms?.find((f) => f.id === firmId)?.name ?? firmId;

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (offer: ComparatorOffer) => {
    setEditing(offer);
    setDialogOpen(true);
  };

  const handleDuplicate = async (offer: ComparatorOffer) => {
    try {
      await duplicateOffer({ id: offer.id });
      toast({ title: "Offer duplicated" });
      refetch();
    } catch (e) {
      toast({ title: (e as Error).message, variant: "destructive" });
    }
  };

  const handleDelete = async (offer: ComparatorOffer) => {
    if (!window.confirm(`Delete offer "${offer.planName}"?`)) return;
    try {
      await deleteOffer({ id: offer.id });
      toast({ title: "Offer deleted" });
      refetch();
    } catch (e) {
      toast({ title: (e as Error).message, variant: "destructive" });
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="w-56">
          <Select value={selectedFirmId} onValueChange={setSelectedFirmId}>
            <SelectTrigger>
              <SelectValue placeholder="All firms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_FIRMS}>All firms</SelectItem>
              {firms?.map((f: ComparatorFirm) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCsvOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Import CSV
          </Button>
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            New offer
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="px-4 py-3 font-medium">Plan</th>
              <th className="px-4 py-3 font-medium">Firm</th>
              <th className="px-4 py-3 font-medium">Size</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Price</th>
              <th className="px-4 py-3 font-medium">Published</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {offers?.map((offer) => (
              <tr key={offer.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3 font-medium text-foreground">{offer.planName}</td>
                <td className="px-4 py-3 text-muted-foreground">{firmName(offer.firmId)}</td>
                <td className="px-4 py-3 font-mono">{offer.accountSizeK}k</td>
                <td className="px-4 py-3 text-muted-foreground">{offer.accountType}</td>
                <td className="px-4 py-3 font-mono">${offer.priceUsd}</td>
                <td className="px-4 py-3">
                  {offer.isPublished ? (
                    <Badge variant="default">Yes</Badge>
                  ) : (
                    <Badge variant="secondary">No</Badge>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(offer)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDuplicate(offer)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(offer)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {offers && offers.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  No offers yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <OfferFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        offer={editing}
        firms={firms ?? []}
        onSaved={() => refetch()}
      />
      <CsvImportDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        onImported={() => refetch()}
      />
    </div>
  );
}
